/**
 * Connect > Adoption dashboard at /connect/adoption.
 *
 * A totals strip (partners, active users, portal orders in the last 30 days)
 * over a per-partner table showing last login, orders (30d / total), and a red
 * "Never signed in" chip for partners whose users have not logged in.
 */

import { Link } from 'react-router-dom';
import { Pill } from '@/components/Pill';
import { ToolPinToggle } from '@/components/ToolPinToggle';
import { gridTheadClassName } from '@ampersandbrands/ui';
import { DataGridShell } from '@/components/grid/DataGridShell';
import { useSession } from '@/lib/useSession';
import { formatInstantDate } from '@/lib/dateUtils';
import {
  hasConnectAdoption,
  isNotAvailable,
  orgTypeLabel,
  useConnectAdoption,
  ORG_TYPE_TONES,
} from '@/lib/useConnect';
import { AccessDeniedCard, ConnectHeader, NotAvailableCard } from './connectShared';

export function ConnectAdoption() {
  const { user } = useSession();
  const canView = hasConnectAdoption(user?.permissions, user?.superAdmin);
  const adoptionQ = useConnectAdoption();

  if (user && !canView) return <AccessDeniedCard />;
  if (isNotAvailable(adoptionQ.error)) {
    return (
      <div className="flex h-full min-h-0 flex-col">
        <ConnectHeader title="Adoption" subtitle="Portal adoption per partner." actions={<ToolPinToggle toolSlug="connect-adoption" toolName="Adoption" />} />
        <NotAvailableCard what="The adoption dashboard" />
      </div>
    );
  }

  const data = adoptionQ.data;
  const totals = data?.totals;

  return (
    <div className="flex h-full min-h-0 flex-col">
      <ConnectHeader
        title="Adoption"
        subtitle="How partners are using the portal: who has signed in, and who is placing orders."
        actions={<ToolPinToggle toolSlug="connect-adoption" toolName="Adoption" />}
      />

      <div className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <KpiBox label="Partners" value={totals?.orgs} />
        <KpiBox label="Active users" value={totals?.active_users} />
        <KpiBox label="Portal orders (30d)" value={totals?.orders_30d} />
      </div>

      <DataGridShell
        total={data?.orgs.length ?? 0}
        loaded={data?.orgs.length ?? 0}
        countNoun="partners"
      >
        <table className="min-w-full text-sm">
          <thead className={gridTheadClassName}>
            <tr>
              <th className="px-3 py-2 text-left font-medium">Partner</th>
              <th className="px-3 py-2 text-left font-medium w-[120px]">Type</th>
              <th className="px-3 py-2 text-right font-medium w-[90px]">Users</th>
              <th className="px-3 py-2 text-left font-medium w-[150px]">Last login</th>
              <th className="px-3 py-2 text-right font-medium w-[110px]">Orders 30d</th>
              <th className="px-3 py-2 text-right font-medium w-[110px]">Orders total</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-amp-line dark:divide-amp-dark-line">
            {adoptionQ.isLoading ? (
              <tr><td colSpan={6} className="px-4 py-6 text-amp-ink-muted dark:text-amp-dark-ink-muted">Loading adoption...</td></tr>
            ) : adoptionQ.error ? (
              <tr><td colSpan={6} className="px-4 py-6 text-red-700 dark:text-red-300">Could not load: {adoptionQ.error.message}</td></tr>
            ) : (data?.orgs.length ?? 0) === 0 ? (
              <tr><td colSpan={6} className="px-4 py-6 text-amp-ink-muted dark:text-amp-dark-ink-muted">No partners yet.</td></tr>
            ) : (
              data!.orgs.map((o) => (
                <tr key={o.id} className="hover:bg-amp-cream-warm dark:hover:bg-amp-dark-surface-warm">
                  <td className="px-3 py-2 text-amp-ink dark:text-amp-dark-ink">
                    <Link to={`/connect/partners/${o.id}`} className="hover:underline">{o.name}</Link>
                  </td>
                  <td className="px-3 py-2"><Pill tone={ORG_TYPE_TONES[o.org_type]}>{orgTypeLabel(o.org_type)}</Pill></td>
                  <td className="px-3 py-2 text-right text-amp-ink dark:text-amp-dark-ink">{o.users}</td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {o.never_signed_in ? (
                      <Pill tone="bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300">Never signed in</Pill>
                    ) : (
                      <span className="text-amp-ink-muted dark:text-amp-dark-ink-muted">{formatInstantDate(o.last_login_at)}</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right text-amp-ink dark:text-amp-dark-ink">{o.orders_30d}</td>
                  <td className="px-3 py-2 text-right text-amp-ink-muted dark:text-amp-dark-ink-muted">{o.orders_total}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </DataGridShell>
    </div>
  );
}

function KpiBox({ label, value }: { label: string; value?: number }) {
  return (
    <div className="rounded-lg border border-amp-line bg-amp-white p-4 dark:border-amp-dark-line dark:bg-amp-dark-surface">
      <p className="text-xs uppercase tracking-wide text-amp-olive dark:text-amp-olive-dark mb-1">{label}</p>
      <p className="text-2xl font-semibold text-amp-ink dark:text-amp-dark-ink">{value != null ? value.toLocaleString() : '--'}</p>
    </div>
  );
}
